/**
 * AppLoadContext.js - Global app data provider
 *
 * Provides app-wide data that needs to be available on any page load
 */
import React from 'react';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { cacheService } from '../services/CacheService';
import spotlightService from '../services/SpotlightService';
import { userDataService } from '../services/UserDataService';
import { useProfile } from './ProfileContext';
import profileService from '../services/ProfileService';
import { useAuthContext } from './AuthContext';
import { clientTokenCache } from '../services/auth/ClientTokenCache';
import { USE_CMS_BACKEND } from '../config/backendEndpoints';
import cmsHomeService from '../services/cms/CmsHomeService';

// Must match SpotlightService's appLoadCacheKey — that is the entry getAppLoad()
// reads and writes. Invalidating anything else leaves the stale payload in place.
const APP_LOAD_CACHE_KEY = 'APP_LOAD_DATA';
const TOKEN_CACHE_KEY = 'APP_TOKEN';
const API_URL_CACHE_KEY = 'API_URL';
const IMAGE_CDN_CACHE_KEY = 'IMAGE_CDN_URL';

const AppLoadContext = createContext();
export const AppLoadProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuthContext();
  const { currentProfile, profiles, hasLoadedProfiles, needsProfileSelection } = useProfile();
  const [categories, setCategories] = useState([]);
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetching = useRef(false);

  // Check if we need to wait for profile selection before showing app content
  const needsProfileSelectionBeforeApp = useMemo(() => {
    // If not authenticated, don't need profile selection
    if (!isAuthenticated) {
      return false;
    }

    // If profiles haven't loaded yet, wait
    if (!hasLoadedProfiles) {
      return true;
    }

    // If we need profile selection, wait
    if (needsProfileSelection()) {
      return true;
    }

    // If we have profiles but no current profile, wait
    if (profiles.length > 0 && !currentProfile) {
      return true;
    }

    const result = false;

    return result;
  }, [isAuthenticated, hasLoadedProfiles, needsProfileSelection, profiles.length, currentProfile]);

  const fetchAppLoad = useCallback(async () => {
    // Outside the try on purpose: inside it, the early return still ran the
    // finally below, disarming the dedupe guard and flipping loading to false
    // while the first fetch was still in flight.
    if (fetching.current) {
      return;
    }
    fetching.current = true;

    try {
      setLoading(true);
      setError(null);

      // Resolve the image CDN base before any rail renders. It comes from
      // /init/config (the same source mobile/TV use), never from a build-time
      // env var, so it is correct per environment and across the DNS cutover.
      // Failures are logged inside primeImageCdnUrl and must not block the app.
      await spotlightService.primeImageCdnUrl();

      // --- New BKFC CMS backend path (REACT_APP_USE_CMS_BACKEND=true) ---
      // Home rails come from CmsHomeService instead of SpotlightService's
      // appload; the Spotlight-specific profile-token priming and hover-modal
      // pre-calculation below are skipped entirely — they depend on DSP
      // profile tokens and slim-event data this backend doesn't produce, and
      // continue_watching/watchlist rail items aren't hydrated with
      // title/image yet (see CmsHomeService's KNOWN GAPS), so pre-calculating
      // hover data for them would only produce broken cards. This is the
      // "flag on -> CmsHomeService" wiring for home rails only; every other
      // Spotlight/DSP path (watchlist, resume, reactions, EPG, ...) is
      // untouched and still flows through spotlightService as before.
      if (USE_CMS_BACKEND) {
        const home = await cmsHomeService.getHome().catch(e => {
          throw new Error(e);
        });
        setCategories(home.categories);
        setAppData(home.raw);
        return;
      }

      // Clear AppLoad cache when profile changes to ensure fresh data
      if (currentProfile?._id) {
        cacheService.invalidate(APP_LOAD_CACHE_KEY);

        // Clear UserDataService data when profile changes
        userDataService.clearCurrentUser();
      }

      // Set the user in SpotlightService before calling getAppLoad
      if (user && isAuthenticated) {
        try {
          // Warm the in-memory client token for the service. getClientToken
          // stores it in ClientTokenCache itself, so no further wiring is needed.
          await clientTokenCache.getClientToken();
        } catch (tokenError) {
          console.warn(
            '🦁 AppLoadContext: Could not preload client token for SpotlightService:',
            tokenError,
          );
        }
        spotlightService.setCurrentUser(user);
        console.log('🦁 AppLoadContext: User set in SpotlightService');
      }

      // If we have a current profile, set it in SpotlightService before appload
      if (currentProfile?._id) {
        console.log('🦁 AppLoadContext: Setting profile token...');

        // Get the profile token using cached method and set it in SpotlightService
        try {
          const profileToken = await spotlightService.getProfileTokenWithCache(currentProfile._id);
          if (profileToken) {
            spotlightService.setCurrentProfileToken(currentProfile._id, profileToken);
            console.log('🦁 AppLoadContext: Profile token set, length:', profileToken.length);
          }
        } catch (error) {
          console.warn(
            '🦁 AppLoadProvider: Failed to get profile token, proceeding without it:',
            error,
          );
        }
      }

      const data = await spotlightService.getAppLoad().catch(e => {
        throw new Error(e);
      });

      // Don't initialize UserDataService with AppLoad user data here
      // Profile-specific data (watchlist, resumptions) will be fetched separately
      // after a profile is selected

      // getAppLoad() already stored the payload under APP_LOAD_CACHE_KEY with its
      // own (deliberately short) content-freshness TTL — storing it a second time
      // here persisted a full duplicate of a multi-MB payload to localStorage.
      // Only the long-lived scalars it extracts are cached below.
      cacheService.set(TOKEN_CACHE_KEY, data.app_data?.token);
      cacheService.set(API_URL_CACHE_KEY, data.app_data?.api_url);
      // Only when appload actually supplies one — an empty app_data must not
      // clobber the value primed from /init/config above.
      if (data.app_data?.image_cdn_url) {
        cacheService.set(IMAGE_CDN_CACHE_KEY, data.app_data.image_cdn_url);
      }

      const processedCategories = await processAppLoad(data);

      setCategories(processedCategories);
      setAppData(data); // Store full app data for other components to use

      // Pre-calculate hover modal data for all channels (this doesn't require profile data).
      // refreshResumptionsData runs the precalc itself once it has the categories,
      // so passing them in replaces the separate preCalculateHoverModalData call
      // that used to redo the whole scan a second time.
      if (processedCategories.length > 0) {
        await userDataService.refreshResumptionsData(processedCategories);
      }
    } catch (err) {
      console.error('Error fetching app load data:', err);
      setError("We couldn't load the app right now. Please refresh and try again.");
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, [currentProfile]); // Add currentProfile as dependency

  useEffect(() => {
    // Only fetch appload data if we don't need profile selection
    if (!needsProfileSelectionBeforeApp) {
      fetchAppLoad();
    } else {
      // Keep loading state true while waiting for profile selection
      setLoading(true);
    }
  }, [user, isAuthenticated, currentProfile?._id, needsProfileSelectionBeforeApp, fetchAppLoad]);

  const value = useMemo(
    () => ({
      categories,
      appData,
      loading,
      error,
      retry: fetchAppLoad,
      // Profile selection check
      needsProfileSelection: needsProfileSelectionBeforeApp,
      // Convenience getters for commonly used data
      token: appData?.app_data?.token,
      apiUrl: appData?.app_data?.api_url,
      imageCdnUrl: appData?.app_data?.image_cdn_url,
      // Convenience method to get hover modal data
      getHoverModalData: channelId => userDataService.getHoverModalData(channelId),
      // Method to refresh hover modal data when resumption data changes
      refreshHoverModalData: async () => {
        if (categories.length > 0) {
          await userDataService.refreshResumptionsData(categories);
        }
      },
      // User data including resumptions
      userData: appData?.user_data,
      resumptions: appData?.user_data?.resumptions,
    }),
    // fetchAppLoad is a dep: without it, `retry` stayed bound to the closure from
    // the first render and re-fetched against a stale profile.
    [categories, appData, loading, error, needsProfileSelectionBeforeApp, fetchAppLoad],
  );

  return <AppLoadContext.Provider value={value}>{children}</AppLoadContext.Provider>;
};

// Custom hook to use the context
export const useAppLoad = () => {
  const context = useContext(AppLoadContext);
  if (!context) {
    throw new Error('useAppLoad must be used within AppLoadProvider');
  }
  return context;
};

// Helper function to process categories
const processAppLoad = async data => {
  if (!data?.homepage?.length) {
    return [];
  }

  const categoriesArray = [];

  // Filter website-enabled categories
  data.homepage.forEach(item => {
    // Process channels to preserve custom_fields
    const processedChannels =
      item.channels?.map(channel => ({
        ...channel,
        // Preserve custom_fields if they exist
        custom_fields: channel.custom_fields || undefined,
      })) || [];

    const processedCategory = {
      _id: item.category._id,
      name: item.category.name,
      display_name: item.category.name,
      channels: processedChannels,
      slug: item.category.slug,
      style: item.category.style, // Include style property for top-number styling
      logo: item.category.logo, // Include logo for category branding
      wallpaper: item.category.wallpaper, // Include wallpaper for category page backgrounds
      custom_fields: item.category.custom_fields || undefined, // Preserve category custom fields
    };

    // Extract style from custom_fields if it exists
    if (item.category.custom_fields && Array.isArray(item.category.custom_fields)) {
      const styleField = item.category.custom_fields.find(field => field.field_title === 'style');
      if (styleField && styleField.field_value) {
        processedCategory.style = styleField.field_value;
      }
    }

    categoriesArray.push(processedCategory);
  });

  return categoriesArray;
};
